import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Backdrop,
  Divider,
} from "@mui/material";

// Import components
import KPISection from "../components/KPISection";
import GraduateSummaryCards from "../components/GraduateSummaryCards";
import GenderCards from "../components/GenderCards";
import GeneralChartsSection from "../components/GeneralChartsSection";
import EmploymentCharts from "../components/EmploymentCharts";
import DemographicsChartsAll from "../components/DemographicsChartsAll";

// Import services
import AllGraduatesAPI from "../services/AllGraduatesAPI";

const AllGraduatesDashboard = () => {
  // State management
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    yearRange: [2020, 2024],
    qualificationLevel: [],
    universities: [],
    yearOfCompletion: [],
    ageRange: [20, 50],
    workExperience: [0, 20],
    countryOfStudy: [],
    gender: [],
    fieldOfStudy: [],
    employmentStatus: [],
    sector: [],
  });

  // Load dashboard data on component mount and when filters change
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await AllGraduatesAPI.fetchDashboardData(filters);
        setDashboardData(data);
        
      } catch (err) {
        setError(err.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [filters]);

  // Destructure dashboard data
  const {
    kpiGraduateData = [],
    kpiEmploymentData = [],
    educationLevelData = [],
    countryStudyData = [],
    employmentByFieldData = [],
    graduatesByGender = [],
    youthGraduatesByGender = [],
    genderEmploymentData = [],
    genderUnemploymentData = [],
    ageDistributionData = [],
    genderByFieldData = [],
    generalChartData = [],
    entryExitData = [],
  } = dashboardData;

  return (
    <Box sx={{ flexGrow: 1, p: 3, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error Message */}
      {error && (
        <Paper
          sx={{ p: 2, mb: 3, bgcolor: "#ffebee", border: "1px solid #f44336" }}
        >
          <Typography variant="body2" color="#d32f2f">
            ⚠️ Error: {error}
          </Typography>
        </Paper>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: "#1976d2", color: "white" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Skills Dashboard: All IPPIS Users (Data from
              IPPIS for both active public Servants and registered users)
            </Typography>
            <Typography variant="subtitle1">
              Comprehensive overview of graduate outcomes 
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Data Source Disclaimer */}
      <Paper
        sx={{ p: 2, mb: 3, bgcolor: "#e3f2fd", border: "1px solid #2196f3" }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", color: "#1565c0", mr: 1 }}
          >
            ℹ️ Data Source Information:
          </Typography>
          <Typography variant="body2" color="#1565c0">
            This dashboard displays data sourced exclusively from the Integrated
            Personnel and Payroll Information System (IPPIS). These figures
            represent only a subset of the country's total education and
            employment landscape and should not be interpreted as comprehensive
            national statistics. The data covers IPPIS-registered personnel and
            graduates only.
          </Typography>
        </Box>
      </Paper>

      {/* Graduate Statistics KPIs */}
      <KPISection
        title="All IPPIS Registered Users Statistics"
        data={kpiGraduateData}
        color="#1976d2"
        icon=""
      />

         {/* Graduate Summary Cards */}
      <GraduateSummaryCards
      educationLevelData={educationLevelData}
      countryStudyData={countryStudyData}
      employmentByFieldData={employmentByFieldData}
      useEmploymentRate = {false}
     />


      {/* Gender Cards */}
      { <GenderCards
        graduatesByGender={graduatesByGender}
        youthGraduatesByGender={youthGraduatesByGender}
        ageDistributionData={ageDistributionData}
      />  }

          {/* Demographics Charts */}
      <DemographicsChartsAll
        genderByFieldData={genderByFieldData}

      />

      {/* Footer Description Card */}
       <Paper
  sx={{
    p: 3,
    mt: 3,
    bgcolor: "#f8f9fa",
    border: "1px solid #e9ecef",
    borderRadius: 2,
  }}
>
  <Box sx={{ textAlign: "center" }}>
    <Typography
      variant="h6"
      gutterBottom
      sx={{ color: "#495057", fontWeight: "bold" }}
    >
      🎓 All Graduates Dashboard Overview
    </Typography>

    <Typography
      variant="body1"
      sx={{ color: "#6c757d", mb: 2, lineHeight: 1.6 }}
    >
      This dashboard provides a comprehensive overview of Rwanda’s graduate
      population, integrating data from the Integrated Personnel and Payroll
      Information System (IPPIS) and national higher education sources.
      It highlights trends in gender, age, and education fields across all
      graduates — whether employed or not — to support evidence-based
      workforce planning and skills development initiatives.
    </Typography>

    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 4,
        flexWrap: "wrap",
        mt: 2,
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ color: "#1976d2", fontWeight: "bold" }}
        >
          Gender Distribution
        </Typography>
        <Typography variant="caption" sx={{ color: "#6c757d" }}>
          Proportion of male and female graduates
        </Typography>
      </Box>

      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ color: "#43a047", fontWeight: "bold" }}
        >
          Youth Graduates
        </Typography>
        <Typography variant="caption" sx={{ color: "#6c757d" }}>
          Focused view on graduates aged 16–30 years
        </Typography>
      </Box>

      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ color: "#f57c00", fontWeight: "bold" }}
        >
          Age Distribution
        </Typography>
        <Typography variant="caption" sx={{ color: "#6c757d" }}>
          Breakdown of graduates across age groups
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ my: 2 }} />

    <Typography
      variant="caption"
      sx={{ color: "#6c757d", fontStyle: "italic" }}
    >
      Last updated: {new Date().toLocaleDateString()} | Data Source: IPPIS-DWH
      (Graduates Registry) | For technical support, contact the System
      Administrator – MIFOTRA
    </Typography>
  </Box>
</Paper>

    </Box>
  );
};

const mapStateToProps = (state) => ({
  // Add any required state mappings
});

export default connect(mapStateToProps)(AllGraduatesDashboard);

